import { useRecoilState } from 'recoil';

import { reporterIsBypassAndSign, ReportStatus, ReportStatuses } from '../state';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { unreachableCaseError } from 'types';
/* eslint-disable import/extensions */
import {
  ActionButton,
  ButtonLabels,
  GetButtonContentParams,
} from './ActionButton/Fields.ActionButton';
import { ButtonContentForIdleStatus } from './ActionButton/ButtonContentForIdleStatus';
import { FieldsButtonProps, SubmitType, VariantToStatusMapType } from './types';

const SIGN_OPTIONS = ['Sign', 'Bypass and Sign'];

const VARIANT_PRIMARY: ReportStatuses[] = [
  ReportStatus.Idle,
  ReportStatus.AddendumAdd,
  ReportStatus.AddendumCancelling,
  ReportStatus.AddendumSubmitting,
  ReportStatus.CancellingPrelim,
  ReportStatus.ProvisionalPrelimSubmit,
  ReportStatus.SubmittingPrelim,
  ReportStatus.Cancelling,
  ReportStatus.Submitting,
];
const VARIANT_YELLOW: ReportStatuses[] = [
  ReportStatus.ProvisionalSubmit,
  ReportStatus.ProvisionalAddendumSubmit,
];
const VARIANT_SECONDARY: ReportStatuses[] = [ReportStatus.Init, ReportStatus.Submitted];

const variantByStatus: VariantToStatusMapType = {
  primary: VARIANT_PRIMARY,
  yellow: VARIANT_YELLOW,
  secondary: VARIANT_SECONDARY,
};

const SUBMIT_BUTTON_LABELS: ButtonLabels = {
  buttonTooltip: 'Cannot sign report until changes are saved.',
  buttonGroupAriaLabel: 'Button group for signing',
  buttonDataTestId: 'sign-button',
  splitButtonDataTestId: 'submit-split-button',
};

export default function FieldsSubmitButton({
  countdownSeconds,
  status,
  onSubmit,
  onCancelSubmit,
  disabled,
  isClaimedByMe,
  isOtherCaseInGroupSubmitting,
  isCountdownMode,
}: FieldsButtonProps): React.ReactElement {
  const [isBypassSignEnabled] = useFeatureFlagEnabled(FF.REPORTER_BYPASS_SIGNING);
  const [, setIsBypassAndSign] = useRecoilState(reporterIsBypassAndSign);
  const isProvisionalPrelimSubmit = status === ReportStatus.ProvisionalPrelimSubmit;

  const getButtonContent = ({
    isRecording,
    isBypass,
  }: GetButtonContentParams): React.ReactElement | string => {
    switch (status) {
      case ReportStatus.Idle:
      case ReportStatus.Init:
      case ReportStatus.CancellingPrelim:
      case ReportStatus.ProvisionalPrelimSubmit:
      case ReportStatus.SubmittingPrelim:
        return (
          <ButtonContentForIdleStatus
            isRecording={isRecording}
            isOtherCaseInGroupSubmitting={isOtherCaseInGroupSubmitting}
            isBypass={isBypass}
          />
        );
      case ReportStatus.Submitting:
        return 'Signing...';
      case ReportStatus.Cancelling:
      case ReportStatus.ProvisionalSubmit:
        // Submit is the default state so this makes it show consistent regardless of the underlying state
        return isClaimedByMe ? 'Cancel Signing' : 'Sign';
      case ReportStatus.Submitted:
        return 'Report Signed';
      case ReportStatus.AddendumAdd:
      case ReportStatus.AddendumSubmitting:
        return isRecording
          ? 'Recording...'
          : isBypass
            ? 'Bypass and Sign Addendum'
            : 'Sign Addendum';
      case ReportStatus.AddendumCancelling:
      case ReportStatus.ProvisionalAddendumSubmit:
        return 'Cancel Addendum';
      default:
        return unreachableCaseError(status);
    }
  };

  return (
    <ActionButton
      countdownSeconds={!isProvisionalPrelimSubmit ? countdownSeconds : null}
      status={status}
      submitType={SubmitType.Final}
      onSubmit={onSubmit}
      onCancelSubmit={onCancelSubmit}
      getContent={getButtonContent}
      variantByStatus={variantByStatus}
      disabled={disabled || isOtherCaseInGroupSubmitting}
      options={SIGN_OPTIONS}
      labels={SUBMIT_BUTTON_LABELS}
      isBypassEnabled={isBypassSignEnabled}
      setBypass={setIsBypassAndSign}
      isCountdownMode={!isProvisionalPrelimSubmit && isCountdownMode}
    />
  );
}

FieldsSubmitButton.displayName = 'FieldsSubmitButton';
